<template>
  <div class="p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">{{ $t('Affecter des colis') }}</h1>
        <p class="text-gray-500">{{ $t('Affectez les colis prepares aux livreurs') }}</p>
      </div>
      <router-link
        to="/stockist-dashboard"
        class="px-4 py-2 border rounded-lg hover:bg-gray-100"
      >
        {{ $t('Retour') }}
      </router-link>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <div class="flex-1 min-w-[200px]">
          <input
            type="text"
            v-model="searchQuery"
            @input="debounceSearch"
            :placeholder="$t('Rechercher par tracking, telephone, nom...')"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          v-model="filters.zone"
          @change="loadPackages"
          class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('Toutes les zones') }}</option>
          <option v-for="zone in zones" :key="zone._id" :value="zone._id">
            {{ zone.name }}
          </option>
        </select>
        <select
          v-model="filters.status"
          @change="loadPackages"
          class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{{ $t('Statut: Prepared/To Prepare') }}</option>
          <option value="all">{{ $t('Tous les statuts') }}</option>
          <option value="prepared">{{ $t('Prepared') }}</option>
          <option value="to prepare">{{ $t('To Prepare') }}</option>
          <option value="shipped">{{ $t('Shipped') }}</option>
        </select>
        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            v-model="filters.ignoreCountry"
            @change="loadPackages"
            class="w-4 h-4"
          />
          {{ $t('Ignorer pays') }}
        </label>
        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            v-model="filters.showAll"
            @change="loadPackages"
            class="w-4 h-4"
          />
          {{ $t('Voir tous (debug)') }}
        </label>
        <button
          @click="loadPackages"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          {{ $t('Actualiser') }}
        </button>
      </div>
      <!-- Debug info -->
      <div v-if="debugInfo" class="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-600">
        <strong>Debug:</strong> Query: {{ JSON.stringify(debugInfo) }}
      </div>
    </div>

    <!-- Assignment Section -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Packages List -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="p-4 border-b flex justify-between items-center">
            <h3 class="text-lg font-semibold">{{ $t('Colis a affecter') }} ({{ packages.length }})</h3>
            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                @change="toggleSelectAll"
                :checked="selectedPackages.length === packages.length && packages.length > 0"
                class="w-4 h-4"
              />
              <span class="text-sm text-gray-500">{{ $t('Tout selectionner') }}</span>
            </div>
          </div>
          <div class="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Tracking') }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Client') }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Zone') }}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Statut') }}</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="loading">
                  <td colspan="5" class="px-4 py-8 text-center">
                    <div class="flex justify-center items-center">
                      <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span class="ml-2">{{ $t('Chargement...') }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-else-if="packages.length === 0">
                  <td colspan="5" class="px-4 py-8 text-center text-gray-500">
                    {{ $t('Aucun colis a affecter') }}
                  </td>
                </tr>
                <tr
                  v-else
                  v-for="pkg in packages"
                  :key="pkg._id"
                  class="hover:bg-gray-50 cursor-pointer"
                  :class="{ 'bg-blue-50': isSelected(pkg._id) }"
                  @click="toggleSelect(pkg._id)"
                >
                  <td class="px-4 py-3">
                    <input
                      type="checkbox"
                      :checked="isSelected(pkg._id)"
                      @click.stop="toggleSelect(pkg._id)"
                      class="w-4 h-4"
                    />
                  </td>
                  <td class="px-4 py-3">
                    <div class="text-sm font-medium text-blue-600">{{ pkg.trackingNumber }}</div>
                    <div class="text-xs text-gray-500">{{ getOrderId(pkg) }}</div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="text-sm font-medium text-gray-900">{{ getCustomerName(pkg) }}</div>
                    <div class="text-xs text-gray-500">{{ getCustomerPhone(pkg) }}</div>
                    <div class="text-xs text-gray-400">{{ getCustomerCity(pkg) }}</div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="text-sm text-gray-900">{{ getZoneName(pkg) }}</div>
                  </td>
                  <td class="px-4 py-3">
                    <span :class="getStatusClass(pkg.status)" class="px-2 py-1 text-xs font-medium rounded-full">
                      {{ pkg.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div class="text-sm text-gray-700">
              {{ $t('Affichage') }} {{ packages.length }} {{ $t('sur') }} {{ paginate.total }} {{ $t('resultats') }}
            </div>
            <div class="flex gap-2">
              <button
                @click="changePage('prev')"
                :disabled="paginate.currentpage <= 1"
                class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {{ $t('Precedent') }}
              </button>
              <span class="px-3 py-1 text-sm">
                {{ paginate.currentpage }} / {{ paginate.lastpage }}
              </span>
              <button
                @click="changePage('next')"
                :disabled="paginate.currentpage >= paginate.lastpage"
                class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {{ $t('Suivant') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Courier Selection Panel -->
      <div class="lg:col-span-1">
        <div class="bg-white rounded-lg shadow p-4 sticky top-4">
          <h3 class="text-lg font-semibold mb-4">{{ $t('Affectation') }}</h3>

          <!-- Selected Count -->
          <div class="mb-4 p-3 bg-blue-50 rounded-lg">
            <p class="text-blue-800">
              <span class="font-semibold">{{ selectedPackages.length }}</span> {{ $t('colis selectionnes') }}
            </p>
          </div>

          <!-- Courier Selection -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Choisir un livreur') }}</label>
            <select
              v-model="selectedCourier"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{{ $t('Selectionner...') }}</option>
              <option v-for="courier in couriers" :key="courier._id" :value="courier._id">
                {{ courier.fullName }} ({{ courier.totalActive || 0 }} {{ $t('actifs') }})
              </option>
            </select>
          </div>

          <!-- Assign Button -->
          <button
            @click="assignPackages"
            :disabled="selectedPackages.length === 0 || !selectedCourier || assigning"
            class="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="assigning">
              <svg class="animate-spin h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ $t('Affectation en cours...') }}
            </span>
            <span v-else>
              {{ $t('Affecter au livreur') }}
            </span>
          </button>

          <!-- Warning -->
          <div class="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p class="text-yellow-800 text-sm">
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              {{ $t('Une fois affectes, seul le Manager Operations peut reassigner les colis.') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <div class="text-center mb-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mt-4">{{ $t('Affectation reussie') }}</h3>
          <p class="text-gray-500 mt-2">{{ assignmentResult.message }}</p>
        </div>
        <div v-if="assignmentResult.results" class="mb-4">
          <p class="text-sm text-green-600">{{ $t('Succes') }}: {{ assignmentResult.results.success ? assignmentResult.results.success.length : 0 }}</p>
          <p v-if="assignmentResult.results.failed && assignmentResult.results.failed.length > 0" class="text-sm text-red-600">
            {{ $t('Echec') }}: {{ assignmentResult.results.failed.length }}
          </p>
          <p v-if="assignmentResult.results.locked && assignmentResult.results.locked.length > 0" class="text-sm text-yellow-600">
            {{ $t('Verrouilles') }}: {{ assignmentResult.results.locked.length }}
          </p>
        </div>
        <button
          @click="closeSuccessModal"
          class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t('Fermer') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AssignPackages',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      assigning: false,
      packages: [],
      couriers: [],
      zones: [],
      selectedPackages: [],
      selectedCourier: '',
      searchQuery: '',
      filters: {
        zone: '',
        status: '',
        ignoreCountry: false,
        showAll: false
      },
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        per_page: 50
      },
      showSuccessModal: false,
      assignmentResult: {},
      searchTimeout: null,
      debugInfo: null
    };
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await Promise.all([
        this.loadPackages(),
        this.loadCouriers(),
        this.loadZones()
      ]);

      // Pre-select courier from URL query parameter
      if (this.$route.query.courier) {
        this.selectedCourier = this.$route.query.courier;
      }
    }
  },
  watch: {
    // Reload when warehouse changes in header
    idWharhouse: {
      handler: function() {
        this.loadPackages();
        this.loadCouriers();
      },
      deep: true
    }
  },
  methods: {
    async loadPackages() {
      this.loading = true;
      this.debugInfo = null;
      try {
        const params = {
          page: this.paginate.currentpage,
          limit: this.paginate.per_page
        };

        // Use selected country from header
        if (this.idWharhouse && this.idWharhouse.country && !this.filters.ignoreCountry) {
          params.country = this.idWharhouse.country;
        }

        if (this.filters.zone) {
          params.zone = this.filters.zone;
        }
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        // Status filter
        if (this.filters.status === 'all') {
          params.allStatuses = true;
        } else if (this.filters.status) {
          params.status = this.filters.status;
        }
        // Ignore country filter for testing
        if (this.filters.ignoreCountry) {
          params.ignoreCountry = true;
          params.ignoreWarehouse = true;
        }
        // Show all packages including those with courier
        if (this.filters.showAll) {
          params.showAll = true;
        }

        console.log('loadPackages params:', params);

        const res = await this.$server.getPackagesToAssign('stockistDashboard', params);
        console.log('loadPackages response:', res);

        if (res && res.content) {
          this.packages = res.content.results || [];
          this.paginate.total = res.content.total || 0;
          this.paginate.lastpage = res.content.last_page || 1;
          this.debugInfo = res.content.query || null;
        } else if (res && res.results) {
          this.packages = res.results || [];
          this.paginate.total = res.total || 0;
          this.paginate.lastpage = res.last_page || 1;
          this.debugInfo = res.query || null;
        }
      } catch (error) {
        console.error('Error loading packages:', error);
        this.packages = [];
      }
      this.loading = false;
    },

    async loadCouriers() {
      try {
        const res = await this.$server.getMyCouriers('stockistDashboard', {});
        if (res && res.content && res.content.couriers) {
          this.couriers = res.content.couriers;
        } else if (res && res.couriers) {
          this.couriers = res.couriers;
        }
      } catch (error) {
        console.error('Error loading couriers:', error);
      }
    },

    async loadZones() {
      try {
        const res = await this.$server.search('zones', { limit: 100 });
        if (res && res.content && res.content.results) {
          this.zones = res.content.results;
        }
      } catch (error) {
        console.error('Error loading zones:', error);
      }
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(function() {
        this.paginate.currentpage = 1;
        this.loadPackages();
      }.bind(this), 500);
    },

    changePage(direction) {
      if (direction === 'next' && this.paginate.currentpage < this.paginate.lastpage) {
        this.paginate.currentpage++;
      } else if (direction === 'prev' && this.paginate.currentpage > 1) {
        this.paginate.currentpage--;
      }
      this.loadPackages();
    },

    isSelected(id) {
      return this.selectedPackages.includes(id);
    },

    toggleSelect(id) {
      const index = this.selectedPackages.indexOf(id);
      if (index === -1) {
        this.selectedPackages.push(id);
      } else {
        this.selectedPackages.splice(index, 1);
      }
    },

    toggleSelectAll() {
      if (this.selectedPackages.length === this.packages.length) {
        this.selectedPackages = [];
      } else {
        this.selectedPackages = this.packages.map(p => p._id);
      }
    },

    async assignPackages() {
      if (this.selectedPackages.length === 0 || !this.selectedCourier) return;

      this.assigning = true;
      try {
        const res = await this.$server.assignToCourier('stockistDashboard', {
          shippingIds: this.selectedPackages,
          courierId: this.selectedCourier
        });

        if (res && (res.content || res.message)) {
          this.assignmentResult = res.content || res;
          this.showSuccessModal = true;
          this.selectedPackages = [];
          this.selectedCourier = '';
          await this.loadPackages();
          await this.loadCouriers();
        }
      } catch (error) {
        console.error('Error assigning packages:', error);
        alert(this.$t('Erreur lors de l\'affectation'));
      }
      this.assigning = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.assignmentResult = {};
    },

    getOrderId(pkg) {
      return pkg && pkg.orderObjet ? pkg.orderObjet.id : '';
    },

    getCustomerName(pkg) {
      if (pkg && pkg.orderObjet && pkg.orderObjet.customer) {
        return pkg.orderObjet.customer.fullName || '';
      }
      return '';
    },

    getCustomerPhone(pkg) {
      if (pkg && pkg.orderObjet && pkg.orderObjet.customer) {
        return pkg.orderObjet.customer.phone || '';
      }
      return '';
    },

    getCustomerCity(pkg) {
      if (pkg && pkg.orderObjet && pkg.orderObjet.customer) {
        return pkg.orderObjet.customer.city || '';
      }
      return '';
    },

    getZoneName(pkg) {
      return pkg && pkg.zone ? pkg.zone.name : '-';
    },

    getStatusClass(status) {
      const classes = {
        'delivered': 'bg-green-100 text-green-800',
        'shipped': 'bg-blue-100 text-blue-800',
        'prepared': 'bg-indigo-100 text-indigo-800',
        'to prepare': 'bg-gray-100 text-gray-800',
        'cancelled': 'bg-red-100 text-red-800',
        'refused': 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    }
  }
};
</script>

<style scoped>
</style>
